import { sub } from "date-fns";
import { useMemo } from "react";

import { UserEvent } from "@/api";
import { EVENT_TYPE_WATCHED, ITEM_TYPE_VIDEO } from "@/const.ts";
import { useUserEvents } from "@/hooks";

export const useRecentlyWatched = () => {
  const { data } = useUserEvents();

  const recentlyWatchedVideoEvents = useMemo(() => {
    if (!data) {
      return data;
    }
    if (!data.Events || data.Events.length === 0) {
      return [];
    }

    const currentDate = new Date();
    const startDate = sub(new Date(), { days: 30 });

    const filteredEvents = data.Events.filter(
      ({ ItemType, EventDate, EventType }) => {
        if (ItemType !== ITEM_TYPE_VIDEO || EventType !== EVENT_TYPE_WATCHED) {
          return false;
        }

        const eventDate = new Date(EventDate);
        return eventDate >= startDate && eventDate <= currentDate;
      },
    ).sort(
      (a, b) =>
        new Date(b.EventDate).getTime() - new Date(a.EventDate).getTime(),
    );

    const uniqueEvents: UserEvent[] = [];
    const itemIds: Set<string> = new Set();

    filteredEvents.forEach((event) => {
      const { ItemId } = event;
      if (!itemIds.has(ItemId)) {
        uniqueEvents.push(event);
        itemIds.add(ItemId);
      }
    });

    return uniqueEvents;
  }, [data]);

  const videoIds = useMemo(() => {
    const videos = new Set<number>();
    const watchedEvents = recentlyWatchedVideoEvents ?? [];
    for (const event of watchedEvents) {
      if (isNaN(Number(event.ItemId))) {
        continue; // skip unexpected item ids
      }
      if (event.ItemType === ITEM_TYPE_VIDEO) {
        videos.add(parseInt(event.ItemId));
      }
    }

    return Array.from(videos.values());
  }, [recentlyWatchedVideoEvents]);

  return {
    data: recentlyWatchedVideoEvents,
    videoIds,
  };
};
